<template>
  <div>
    <!-- Componente de Notificações -->
    <div class="relative inline-block" ref="notificationContainer">
      <!-- Ícone de Sino -->
      <button
        type="button"
        class="relative flex items-center justify-center rounded-full p-2 transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-white/10"
        :class="{ 'text-blue-500': hasUnreadNotifications }"
        :disabled="!props.areNotificationsEnabled"
        aria-label="Notificações"
        :aria-expanded="isNotificationPanelOpen"
        @click="toggleNotificationPanel"
      >
        <span class="relative inline-flex">
          <Bell class="h-6 w-6 text-blue-500" :class="{ 'animate-pulse-slow': loading && firstWebSocketConnect }" />
          <Lock v-if="!props.areNotificationsEnabled" class="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full bg-white p-0.5 text-red-500 shadow dark:bg-gray-800" />
          <RefreshCw v-if="loading && firstWebSocketConnect" class="absolute -right-1 -top-1 h-3.5 w-3.5 animate-spin text-blue-400" />
          <span
            v-if="hasUnreadNotifications"
            class="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
            aria-label="Notificações não lidas"
          >
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </span>
      </button>

      <!-- Painel de Notificações (Popup) -->
      <Transition name="fade-scale">
        <div
          v-if="isNotificationPanelOpen"
          class="card z-50 flex max-h-[32rem] flex-col overflow-hidden shadow-xl"
          :class="isMobile ? 'fixed inset-x-3 top-16' : 'absolute right-0 top-full mt-2 w-80'"
        >
          <div class="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Notificações</h3>
            <button type="button" class="rounded px-2 py-1 text-sm text-blue-500 transition hover:bg-blue-50 disabled:opacity-50 dark:hover:bg-blue-900/30" :disabled="unreadCount === 0" @click="markAllAsRead">
              Marcar todas como lidas
            </button>
          </div>

          <!-- Abas -->
          <div class="flex border-b border-gray-200 dark:border-gray-700" role="tablist">
            <button
              type="button"
              role="tab"
              class="flex-1 border-b-2 px-4 py-2 text-sm font-medium transition"
              :class="activeTab === 'unread' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'"
              :aria-selected="activeTab === 'unread'"
              @click="activeTab = 'unread'"
            >
              Não lidas
              <span class="ml-1 rounded-full bg-gray-200 px-1.5 text-xs dark:bg-gray-700">{{ unreadCount }}</span>
            </button>
            <button
              type="button"
              role="tab"
              class="flex-1 border-b-2 px-4 py-2 text-sm font-medium transition"
              :class="activeTab === 'read' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'"
              :aria-selected="activeTab === 'read'"
              @click="showReadNotifications"
            >
              Lidas
            </button>
          </div>

          <!-- Lista -->
          <div class="flex-1 overflow-y-auto">
            <div v-if="loading" class="flex flex-col items-center gap-2 p-8 text-sm text-gray-500 dark:text-gray-400">
              <RefreshCw class="h-6 w-6 animate-spin text-blue-500" />
              <span>Carregando notificações...</span>
            </div>

            <div v-else-if="filteredNotifications.length === 0" class="flex flex-col items-center gap-2 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              <Bell class="h-8 w-8 text-gray-300 dark:text-gray-600" />
              <p>Nenhuma notificação {{ activeTab === 'unread' ? 'não lida' : 'lida' }}</p>
            </div>

            <ul v-else class="divide-y divide-gray-100 dark:divide-gray-700">
              <li
                v-for="notification in filteredNotifications"
                :key="notification.id"
                class="flex cursor-pointer gap-3 p-3 transition hover:bg-gray-50 dark:hover:bg-gray-700/50"
                :class="{ 'bg-blue-50/60 dark:bg-blue-900/10': !notification.read }"
                @click="handleNotificationClick(notification)"
              >
                <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" :class="iconClasses(notification.type)">
                  <component :is="getNotificationIcon(notification.type)" class="h-5 w-5" />
                </div>

                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <h4 class="truncate text-sm font-semibold text-gray-900 dark:text-white">{{ notification.title }}</h4>
                    <div class="flex shrink-0 items-center gap-1.5">
                      <span class="text-xs text-gray-400">{{ formatTime(notification.time) }}</span>
                      <span v-if="!notification.read" class="h-2 w-2 rounded-full bg-blue-500" aria-hidden="true"></span>
                    </div>
                  </div>
                  <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{{ truncateMessage(stripAsterisks(notification.message)) }}</p>
                  <div class="mt-1 flex flex-wrap gap-3">
                    <button v-if="notification.message.length > 60" type="button" class="text-xs font-medium text-blue-500 hover:underline" @click.stop="openMessageModal(notification)">Ver mais</button>
                    <button v-if="!notification.read" type="button" class="text-xs font-medium text-gray-500 hover:text-blue-500 dark:text-gray-400" @click.stop="markAsRead(notification)">Marcar como lida</button>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div class="border-t border-gray-200 p-2 dark:border-gray-700">
            <button type="button" class="btn-ghost w-full text-sm text-blue-600 dark:text-blue-400" @click="openFullHistory">
              <History class="h-4 w-4" /> Histórico completo
            </button>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Histórico Completo -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showFullHistory" class="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="history-title">
          <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="showFullHistory = false"></div>

          <div class="relative flex h-full w-full flex-col bg-white shadow-2xl animate-scale-in dark:bg-gray-800 sm:h-auto sm:max-h-[85vh] sm:max-w-3xl sm:rounded-2xl">
            <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-6">
              <h2 id="history-title" class="text-lg font-semibold text-gray-900 dark:text-white">Histórico de Notificações</h2>
              <button type="button" class="btn-ghost p-2" aria-label="Fechar histórico" @click="showFullHistory = false">
                <X class="h-6 w-6" />
              </button>
            </div>

            <div class="flex min-h-0 flex-1 flex-col">
              <!-- Filtros e Pesquisa -->
              <div class="flex flex-col gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:flex-row sm:items-center sm:px-6">
                <label class="relative flex-1">
                  <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input v-model="searchQuery" type="search" placeholder="Pesquisar notificações..." class="input pl-9" />
                </label>
                <div class="grid grid-cols-2 gap-2 sm:flex">
                  <select v-model="filterType" class="input py-2" aria-label="Filtrar por tipo">
                    <option value="all">Todos os tipos</option>
                    <option value="comentario_evento">Comentários</option>
                    <option value="manutencao">Manutenção</option>
                    <option value="update">Atualizações</option>
                    <option value="feature">Novidades</option>
                  </select>
                  <select v-model="filterStatus" class="input py-2" aria-label="Filtrar por status">
                    <option value="all">Todos os status</option>
                    <option value="read">Lidas</option>
                    <option value="unread">Não lidas</option>
                  </select>
                </div>
              </div>

              <!-- Lista -->
              <div class="min-h-0 flex-1 overflow-y-auto">
                <div v-if="loading" class="flex flex-col items-center gap-2 p-10 text-sm text-gray-500 dark:text-gray-400">
                  <RefreshCw class="h-6 w-6 animate-spin text-blue-500" />
                  <span>Carregando notificações...</span>
                </div>

                <div v-else-if="filteredHistoryNotifications.length === 0" class="flex flex-col items-center gap-2 p-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  <Search class="h-8 w-8 text-gray-300 dark:text-gray-600" />
                  <p>Nenhuma notificação encontrada</p>
                </div>

                <ul v-else class="divide-y divide-gray-100 dark:divide-gray-700">
                  <li
                    v-for="notification in filteredHistoryNotifications"
                    :key="notification.id"
                    class="flex gap-3 p-4 sm:px-6"
                    :class="{ 'bg-blue-50/60 dark:bg-blue-900/10': !notification.read }"
                  >
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" :class="iconClasses(notification.type)">
                      <component :is="getNotificationIcon(notification.type)" class="h-6 w-6" />
                    </div>

                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
                        <h4 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                          {{ notification.title }}
                          <span v-if="!notification.read" class="h-2 w-2 rounded-full bg-blue-500" aria-hidden="true"></span>
                        </h4>
                        <span class="text-xs text-gray-400">{{ formatDate(notification.time) }}</span>
                      </div>
                      <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">{{ truncateMessage(stripAsterisks(notification.message), 100) }}</p>
                      <div class="mt-2 flex flex-wrap gap-3">
                        <button v-if="notification.message.length > 100" type="button" class="text-xs font-medium text-blue-500 hover:underline" @click.stop="openMessageModal(notification)">Ver mais</button>
                        <button v-if="!notification.read" type="button" class="text-xs font-medium text-gray-500 hover:text-blue-500 dark:text-gray-400" @click.stop="markAsRead(notification)">Marcar como lida</button>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal: mensagem completa -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showMessageModal" class="fixed inset-0 z-[110] flex items-end justify-center p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="message-title">
          <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="showMessageModal = false"></div>

          <div class="card relative flex max-h-[85vh] w-full flex-col overflow-hidden rounded-b-none rounded-t-2xl animate-fade-in-up sm:max-w-lg sm:rounded-2xl">
            <div class="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-6">
              <h3 id="message-title" class="text-base font-semibold text-gray-900 dark:text-white">{{ selectedNotification?.title }}</h3>
              <button type="button" class="btn-ghost p-1.5" aria-label="Fechar mensagem" @click="showMessageModal = false">
                <X class="h-5 w-5" />
              </button>
            </div>

            <div class="prose prose-sm max-w-none overflow-y-auto px-4 py-4 text-gray-700 dark:prose-invert dark:text-gray-200 sm:px-6" v-html="formatNotificationMessage(selectedNotification?.message || '')"></div>

            <div class="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400 sm:px-6">
              <span>{{ selectedNotification ? formatDate(selectedNotification.time) : '' }}</span>
              <div v-if="selectedNotification" class="badge" :class="iconClasses(selectedNotification.type)">
                <component :is="getNotificationIcon(selectedNotification.type)" class="h-3.5 w-3.5" />
                <span>{{ getNotificationTypeName(selectedNotification.type) }}</span>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Overlay para fechar o painel quando clicar fora -->
    <div v-if="isNotificationPanelOpen" class="fixed inset-0 z-40" @click="closeNotificationPanel"></div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed, defineProps} from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/stores/notifications/useNotificationStore'
import {
  Bell, X, History, Search, RefreshCw, Zap, Clock, Lock,
  Gauge, MessageSquare, AlertTriangle, Info
} from 'lucide-vue-next'

const router = useRouter()
// 📦 Store
const notificationStore = useNotificationStore()
// 🧠 Estado
const isNotificationPanelOpen = ref(false)
const activeTab = ref('unread')
const showFullHistory = ref(false)
const showMessageModal = ref(false)
const searchQuery = ref('')
const filterType = ref('all')
const filterStatus = ref('all')
const notificationContainer = ref(null)
const isMobile = ref(false)
const selectedNotification = ref(null)

// 🚀 Computeds
const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)
const hasUnreadNotifications = computed(() => unreadCount.value > 0)
const loading = computed(() => notificationStore.loading)
const firstWebSocketConnect = computed(() => notificationStore.firstWebSocketConnect)

// 📦 Marcar como lida
const markAsRead = (notification) => {
  notificationStore.markAsRead(notification)
}

const markAllAsRead = () => {
  notificationStore.markAllAsRead()
}

// 🔍 Filtros e Histórico
const filteredNotifications = computed(() =>
  notifications.value
    .filter(n => activeTab.value === 'unread' ? !n.read : n.read)
    .sort((a, b) => b.time - a.time)
)

const filteredHistoryNotifications = computed(() => {
  let result = [...notifications.value]
  if (filterStatus.value === 'read') result = result.filter(n => n.read)
  else if (filterStatus.value === 'unread') result = result.filter(n => !n.read)
  if (filterType.value !== 'all') result = result.filter(n => n.type === filterType.value)
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(n =>
      n.title.toLowerCase().includes(q) ||
      n.message.toLowerCase().includes(q)
    )
  }
  return result.sort((a, b) => b.time - a.time)
})

// 📦 Panel
const toggleNotificationPanel = () => {
  isNotificationPanelOpen.value = !isNotificationPanelOpen.value
  if (isNotificationPanelOpen.value) {
    if (unreadCount.value > 0) activeTab.value = 'unread'
  }
}

const closeNotificationPanel = (e) => {
  if (notificationContainer.value && !notificationContainer.value.contains(e.target)) {
    isNotificationPanelOpen.value = false
  }
}

const openFullHistory = () => {
  isNotificationPanelOpen.value = false
  showFullHistory.value = true
  document.body.classList.add('overflow-hidden')
}

// 📩 Ações
const openMessageModal = (notification) => {
  selectedNotification.value = notification
  showMessageModal.value = true
  if (!notification.read) markAsRead(notification)
}

const handleNotificationClick = async (notification) => {
  if (!notification.read) {
    await markAsRead(notification)
  }
  if (notification.link) {
    if (notification.link.startsWith('/')) router.push(notification.link)
    else window.location.href = notification.link
  } else {
    openMessageModal(notification)
  }
}

// 🛠️ Helpers
const stripAsterisks = (message) => {
  if (!message) return ''
  return message.replace(/\*/g, '')
}

const truncateMessage = (message, length = 60) => {
  if (!message) return ''
  return message.length > length ? message.substring(0, length) + '...' : message
}

const formatNotificationMessage = (message) => {
  if (!message) return ''
  const parts = message.split('*')
  let bold = false
  const result = []
  for (const part of parts) {
    if (bold) {
      result.push(`<strong>${part}</strong>`)
    } else {
      result.push(part)
    }
    bold = !bold
  }
  return result.join('')
}

const getNotificationType = (type) => {
  switch (type) {
    case 'update': return 'update'
    case 'feature': return 'feature'
    case 'maintenance':
    case 'manutencao': return 'maintenance'
    case 'performance': return 'performance'
    case 'comentario_evento': return 'comment'
    case 'error':
    case 'warning': return 'warning'
    default: return 'default'
  }
}

// Cores do ícone por tipo (antes eram classes .notification-icon-* no <style>)
const NOTIFICATION_ICON_CLASSES = {
  default: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  update: 'bg-blue-100 text-blue-500 dark:bg-blue-500/20 dark:text-blue-400',
  feature: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
  maintenance: 'bg-indigo-100 text-indigo-500 dark:bg-indigo-500/20 dark:text-indigo-400',
  performance: 'bg-emerald-100 text-emerald-500 dark:bg-emerald-500/20 dark:text-emerald-400',
  comment: 'bg-purple-100 text-purple-500 dark:bg-purple-500/20 dark:text-purple-400',
  warning: 'bg-red-100 text-red-500 dark:bg-red-500/20 dark:text-red-400',
}
const iconClasses = (type) => NOTIFICATION_ICON_CLASSES[getNotificationType(type)] || NOTIFICATION_ICON_CLASSES.default

const getNotificationTypeName = (type) => {
  switch (type) {
    case 'update': return 'Atualização'
    case 'feature': return 'Novidade'
    case 'maintenance':
    case 'manutencao': return 'Manutenção'
    case 'performance': return 'Desempenho'
    case 'comentario_evento': return 'Comentário'
    case 'error':
    case 'warning': return 'Alerta'
    default: return 'Notificação'
  }
}

const getNotificationIcon = (type) => {
  switch (type) {
    case 'update': return RefreshCw
    case 'feature': return Zap
    case 'maintenance':
    case 'manutencao': return Clock
    case 'performance': return Gauge
    case 'comentario_evento': return MessageSquare
    case 'error':
    case 'warning': return AlertTriangle
    case 'info': return Info
    default: return Bell
  }
}

const props = defineProps({
  areNotificationsEnabled: {
    type: Boolean,
    default: true
  }
})

const formatTime = (date) => {
  const now = new Date()
  const diff = Math.floor((now - date) / 1000)
  if (diff < 60) return `${diff}s`
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`
  return formatDate(date, true)
}

const formatDate = (date, short = false) => {
  if (!date) return ''
  // Se não for Date, tenta converter
  const d = (date instanceof Date) ? date : new Date(date)
  if (isNaN(d)) return ''
  if (short) return d.toLocaleDateString('pt-BR')
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

// 🧠 Responsivo
const checkScreenSize = () => {
  isMobile.value = window.innerWidth < 768
}

// 🚀 Lifecycle
onMounted(() => {
  window.addEventListener('resize', checkScreenSize)
  checkScreenSize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize)
  document.body.classList.remove('overflow-hidden')
})

const showReadNotifications = () => {
  if (notificationStore.socket && notificationStore.socket.readyState === 1) {
    notificationStore.socket.send(JSON.stringify({ action: 'get_read' }))
  }
  activeTab.value = 'read'
}
</script>
